import {
  Card,
  HStack,
  VStack,
  Text,
  Heading,
  Badge,
  IconButton,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Portal,
} from '@chakra-ui/react'
import { Home, MapPin, Ruler, User, MoreVertical, Eye, Trash2, ArrowRightLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { UnitWithResidence } from '../types'

interface UnitCardProps {
  unit: UnitWithResidence
  isSelected?: boolean
  onClick?: (id: string) => void
  onDelete?: (id: string) => void
  onManageMutations?: (id: string) => void
}

export function UnitCard({ unit, isSelected, onClick, onDelete, onManageMutations }: UnitCardProps) {
  const { t } = useTranslation()

  const handleClick = () => {
    onClick?.(unit.id)
  }

  const residenceName = unit.residences
    ? unit.residences.residence_key
      ? `${unit.residences.residence_key} - ${unit.residences.name}`
      : unit.residences.name
    : undefined

  const getOwnerDisplay = () => {
    if (!unit.ownerships || unit.ownerships.length === 0) return null
    const owners = unit.ownerships
      .filter((o) => o.owners)
      .map((o) => `${o.owners!.first_name} ${o.owners!.last_name}`)
    if (owners.length === 0) return null
    if (owners.length === 1) return owners[0]
    return `${owners[0]} +${owners.length - 1}`
  }

  const ownerName = getOwnerDisplay()

  const getUnitTypeColor = (type: string | null) => {
    switch (type) {
      case 'RESIDENTIAL':
        return 'blue'
      case 'PARKING':
        return 'purple'
      case 'CELLAR':
        return 'gray'
      case 'COMMERCIAL':
        return 'green'
      default:
        return 'gray'
    }
  }

  return (
    <Card.Root
      data-unit-card={unit.id}
      onClick={handleClick}
      cursor="pointer"
      borderWidth="2px"
      borderColor={isSelected ? 'brand.800' : 'transparent'}
      _hover={{
        shadow: 'md',
        transform: 'translateY(-2px)',
        borderColor: isSelected ? 'brand.900' : 'gray.200',
      }}
      transition="all 0.2s"
    >
      <Card.Header pb={2}>
        <HStack justify="space-between" align="start">
          <VStack align="stretch" gap={1}>
            <HStack gap={2}>
              <Home size={20} />
              <Heading size="md">Lot {unit.unit_number}</Heading>
            </HStack>
          </VStack>

          {/* Actions menu - aligned top right */}
          <div onClick={(e) => e.stopPropagation()}>
            <MenuRoot>
              <MenuTrigger asChild>
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label={t('common.actions')}
                >
                  <MoreVertical size={16} />
                </IconButton>
              </MenuTrigger>
              <Portal>
                <MenuPositioner>
                  <MenuContent minW="180px">
                    <MenuItem
                      value="details"
                      onClick={(e) => {
                        e.stopPropagation()
                        onClick?.(unit.id)
                      }}
                    >
                      <HStack gap={2}>
                        <Eye size={16} />
                        <Text>{t('common.details')}</Text>
                      </HStack>
                    </MenuItem>
                    {onManageMutations && (
                      <MenuItem
                        value="mutations"
                        onClick={(e) => {
                          e.stopPropagation()
                          onManageMutations(unit.id)
                        }}
                      >
                        <HStack gap={2}>
                          <ArrowRightLeft size={16} />
                          <Text>Gérer les mutations</Text>
                        </HStack>
                      </MenuItem>
                    )}
                    {onDelete && (
                      <MenuItem
                        value="delete"
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete(unit.id)
                        }}
                        color="red.600"
                        _hover={{ bg: 'red.50' }}
                      >
                        <HStack gap={2}>
                          <Trash2 size={16} />
                          <Text>{t('common.delete')}</Text>
                        </HStack>
                      </MenuItem>
                    )}
                  </MenuContent>
                </MenuPositioner>
              </Portal>
            </MenuRoot>
          </div>
        </HStack>
      </Card.Header>

      <Card.Body pt={2}>
        <VStack align="stretch" gap={3}>
          {/* Type and Floor */}
          <HStack gap={2} flexWrap="wrap">
            {unit.unit_type && (
              <Badge colorPalette={getUnitTypeColor(unit.unit_type)} size="sm">
                {t(`units.unit_types.${unit.unit_type}`)}
              </Badge>
            )}
            {unit.floor !== null && (
              <Badge colorPalette="gray" size="sm">
                <MapPin size={10} />
                <Text fontSize="xs">
                  {unit.floor === 0
                    ? 'RDC'
                    : unit.floor < 0
                    ? `S-${Math.abs(unit.floor)}`
                    : `${unit.floor}e`}
                </Text>
              </Badge>
            )}
            {unit.stairwell && (
              <Badge colorPalette="gray" size="sm" variant="subtle">
                Escalier {unit.stairwell}
              </Badge>
            )}
          </HStack>

          {/* Residence */}
          {residenceName && (
            <HStack gap={2}>
              <Text fontSize="sm" color="gray.600">
                {residenceName}
              </Text>
            </HStack>
          )}

          {/* Area */}
          {unit.area_sqm && (
            <HStack gap={2}>
              <Ruler size={14} color="var(--chakra-colors-gray-500)" />
              <Text fontSize="sm" color="gray.600">
                {unit.area_sqm} m²
              </Text>
            </HStack>
          )}

          {/* Owner */}
          {ownerName && (
            <HStack gap={2}>
              <User size={14} color="var(--chakra-colors-gray-500)" />
              <Text fontSize="sm" color="gray.600">
                {ownerName}
              </Text>
            </HStack>
          )}

          {/* Shares */}
          <HStack justify="space-between" pt={2} borderTopWidth="1px" borderColor="gray.200">
            <VStack align="flex-start" gap={0}>
              <Text fontSize="xs" color="gray.500">
                Tantièmes
              </Text>
              <Text fontSize="md" fontWeight="semibold">
                {unit.general_shares || 0}
              </Text>
            </VStack>
            {unit.work_fund_share !== null && (
              <VStack align="flex-end" gap={0}>
                <Text fontSize="xs" color="gray.500">
                  Fonds travaux
                </Text>
                <Text fontSize="md" fontWeight="semibold">
                  {unit.work_fund_share || 0}
                </Text>
              </VStack>
            )}
          </HStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}
